import { Loader2 } from 'lucide-react';
import React from 'react';

import { useTokenPairStore } from '@/features/tokenPairs';
import { cn } from '@/lib/utils';

import type { PriceConfig } from '../hooks/usePriceConfig';
import { useTradeQuote } from '../hooks/useTradeQuote';
import { useTradingStore } from '../store';

interface TradePreviewProps {
    currentValue: number;
    priceConfig: PriceConfig;
}

function formatSize(size: number | null): string {
    if (size === null || !Number.isFinite(size)) return '—';
    const abs = Math.abs(size);
    const decimals = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
    return size.toFixed(decimals);
}

function formatUsd(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}`;
}

const Cell: React.FC<{ label: string; value: string; className?: string }> = ({
    label,
    value,
    className,
}) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {label}
        </span>
        <span
            className={cn(
                'font-mono text-[11px] tabular-nums text-foreground/90',
                className,
            )}
        >
            {value}
        </span>
    </div>
);

/**
 * Pre-trade order preview shown above the LONG/SHORT buttons. Surfaces the
 * `POST /speed/quote` sizing (position size, notional, slippage-padded order
 * value) plus an estimated liquidation price for each direction so the user
 * isn't opening blind. "Entry ≈" is the live mark — see TradeQuote.limitPx for
 * why the quote's padded limit price must not be shown there.
 */
const TradePreview: React.FC<TradePreviewProps> = ({
    currentValue,
    priceConfig,
}) => {
    const betAmount = useTradingStore((s) => s.betAmount);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const quote = useTradeQuote(currentValue);

    if (betAmount <= 0) return null;

    return (
        <div className="mb-3 rounded-lg border border-border/60 bg-secondary/30 p-2.5">
            <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Order preview
                </span>
                {quote.isLoading && (
                    <Loader2
                        size={10}
                        className="animate-spin text-muted-foreground"
                    />
                )}
            </div>
            <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                <Cell
                    label="Size"
                    value={`${formatSize(quote.sizeAsset)} ${selectedCoin}`}
                />
                <Cell label="Notional" value={formatUsd(quote.notionalUsd)} />
                <Cell
                    label="Order Value"
                    value={formatUsd(quote.orderValueUsd)}
                />
                <Cell
                    label="Entry ≈"
                    value={
                        quote.markPx !== null
                            ? `$${priceConfig.formatDisplay(quote.markPx)}`
                            : '—'
                    }
                />
                <Cell
                    label="Est. liq · Long"
                    value={
                        quote.estLiqLong !== null
                            ? `$${priceConfig.formatDisplay(quote.estLiqLong)}`
                            : '—'
                    }
                    className="text-destructive"
                />
                <Cell
                    label="Est. liq · Short"
                    value={
                        quote.estLiqShort !== null
                            ? `$${priceConfig.formatDisplay(quote.estLiqShort)}`
                            : '—'
                    }
                    className="text-destructive"
                />
            </div>
        </div>
    );
};

export default TradePreview;
